import type { MetaFunction } from "@remix-run/cloudflare";
import { useState } from "react";
import { Link, useNavigate } from "@remix-run/react";
import { Mail, Lock, Eye, EyeOff, ArrowRight } from "lucide-react";
import { SplashScreen } from "~/components/SplashScreen";
import styles from "~/styles/auth.module.css";

export const meta: MetaFunction = () => [
  { title: "Sign In — Naomi" },
  { name: "description", content: "Sign in to Naomi, your AI connection layer by Prodigium Dynamics." },
];

/** Derive a friendly display name from the email local-part (e.g. "lucy.b@x" → "Lucy"). */
function nameFromEmail(email: string): string {
  const local = email.split("@")[0] ?? "";
  const first = local.split(/[._-]/)[0] ?? "";
  if (!first) return "there";
  return first.charAt(0).toUpperCase() + first.slice(1);
}

export default function NaomiLoginPage() {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!email.trim() || !password.trim()) {
      setError("Please enter your email and password.");
      return;
    }

    setLoading(true);
    // No auth backend yet — this is a UI preview. Stash a display name so the
    // chat can greet the user, then drop them into the conversation.
    setTimeout(() => {
      try {
        sessionStorage.setItem("naomi_user", nameFromEmail(email));
      } catch {
        /* sessionStorage may be unavailable — non-fatal */
      }
      navigate("/naomi/chat");
    }, 900);
  };

  return (
    <>
      <SplashScreen name="NAOMI" subtitle="By Prodigium Dynamics" dark />
      <div className={styles.layout}>
        <div className={styles.content}>
          <div className={styles.card}>
            <div className={styles.header}>
              <p className={styles.tag}>Prodigium · Naomi</p>
              <h1 className={styles.title}>Hey, it&apos;s Naomi</h1>
              <p className={styles.subtitle}>Sign in to pick up where you left off. I&apos;m here whenever you&apos;re ready.</p>
            </div>

            {error && (
              <div className={styles.error} style={{ marginBottom: 16 }}>
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className={styles.form}>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="email">Email</label>
                <div className={styles.inputWrap}>
                  <span className={styles.icon}><Mail size={14} strokeWidth={1.5} /></span>
                  <input
                    id="email"
                    type="email"
                    className={styles.input}
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                    required
                  />
                </div>
              </div>

              <div className={styles.field}>
                <label className={styles.label} htmlFor="password">Password</label>
                <div className={styles.inputWrap}>
                  <span className={styles.icon}><Lock size={14} strokeWidth={1.5} /></span>
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    className={styles.input}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    required
                  />
                  <button
                    type="button"
                    className={styles.toggleBtn}
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <EyeOff size={14} strokeWidth={1.5} /> : <Eye size={14} strokeWidth={1.5} />}
                  </button>
                </div>
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: -10 }}>
                <Link
                  to="/account/forgot-password"
                  style={{
                    fontSize: 10,
                    letterSpacing: "0.06em",
                    color: "rgba(255,255,255,0.45)",
                    textDecoration: "none",
                    transition: "color 0.2s",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = "rgba(255,255,255,0.85)")}
                  onMouseLeave={(e) => (e.currentTarget.style.color = "rgba(255,255,255,0.45)")}
                >
                  Forgot password?
                </Link>
              </div>

              <div className={styles.actions}>
                <button type="submit" className={styles.submitBtn} disabled={loading}>
                  {loading ? (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                      <span style={{ width: 14, height: 14, border: "2px solid rgba(0,0,0,0.2)", borderTopColor: "#000", borderRadius: "50%", animation: "spin 0.8s linear infinite", display: "inline-block" }} />
                      Opening…
                    </span>
                  ) : (
                    <>
                      Continue to Naomi <ArrowRight size={14} strokeWidth={2} />
                    </>
                  )}
                </button>
              </div>
            </form>

            <div className={styles.divider}>
              <span className={styles.dividerLine} />
              <span className={styles.dividerText}>or</span>
              <span className={styles.dividerLine} />
            </div>

            <div className={styles.socialRow}>
              <button type="button" className={styles.socialBtn} onClick={() => setError("Google Sign-In is not yet connected.")}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                Google
              </button>
              <button type="button" className={styles.socialBtn} onClick={() => setError("GitHub Sign-In is not yet connected.")}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0 0 24 12c0-6.63-5.37-12-12-12z"/>
                </svg>
                GitHub
              </button>
            </div>

            <div className={styles.footerLinks}>
              <p className={styles.footerText}>
                New here?{" "}
                <Link to="/account/register" className={styles.link}>Create an account</Link>
              </p>
            </div>
          </div>

          <Link to="/" className={styles.backLink} style={{ marginTop: 24 }}>
            ← Back to OS
          </Link>
        </div>
      </div>
    </>
  );
}
